import { Menu, MessageSquare, Search, X } from 'lucide-react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { BrandMark } from './BrandMark';

export function MarketplaceLayout({ snapshot, currentUser, children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const mainRef = useRef(null);
  const [headerQuery, setHeaderQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const main = mainRef.current;
    if (!main) {
      return undefined;
    }

    const selector = [
      '.scroll-reveal',
      '.page-head',
      '.section-block',
      '.simple-hero',
      '.simple-cta',
      '.detail-panel',
      '.auth-card',
      '.listing-showcase',
      '.listing-grid',
      '.simple-category-grid',
      '.simple-steps-grid',
      '.loved-by-grid',
      '.account-tabs',
      '.account-orders-board',
      '.account-billing-grid',
      '.account-reviews-grid',
      '.account-listing-card',
      '.account-billing-card',
      '.account-review-card',
      '.account-review-item',
      '.message-thread-card',
      '.order-message-item',
      '.order-file-item',
      '.faq-item',
      '.simple-listing-card',
      '.category-icon-card',
      '.simple-step-card',
    ].join(',');
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const targets = new Set();
    let observer = null;

    function prepareTarget(target) {
      if (!(target instanceof HTMLElement) || target.classList.contains('no-reveal')) {
        return;
      }

      target.classList.add('scroll-reveal');
      targets.add(target);

      if (target.classList.contains('reveal-on-load')) {
        window.requestAnimationFrame(() => target.classList.add('is-visible'));
      } else if (prefersReducedMotion || !observer) {
        target.classList.add('is-visible');
      } else {
        observer.observe(target);
      }
    }

    if (!prefersReducedMotion && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              observer.unobserve(entry.target);
            }
          });
        },
        {
          rootMargin: '0px 0px -8% 0px',
          threshold: 0.12,
        }
      );
    }

    main.querySelectorAll(selector).forEach(prepareTarget);

    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) {
            return;
          }

          if (node.matches(selector)) {
            prepareTarget(node);
          }

          node.querySelectorAll(selector).forEach(prepareTarget);
        });
      });
    });

    mutationObserver.observe(main, { childList: true, subtree: true });

    return () => {
      mutationObserver.disconnect();
      if (observer) {
        observer.disconnect();
      }
      targets.forEach((target) => {
        target.classList.remove('scroll-reveal', 'is-visible');
      });
    };
  }, [location.pathname, location.search]);

  function submitSearch(event) {
    event.preventDefault();
    const query = headerQuery.trim();
    navigate(query ? `/explore?q=${encodeURIComponent(query)}` : '/explore');
    setMobileMenuOpen(false);
  }

  function closeMobileMenu() {
    setMobileMenuOpen(false);
  }

  return (
    <div className="app-shell">
      <header className="site-header">
        <div className="site-header-main">
          <div className="site-header-left">
            <Link to="/" aria-label="AgenticHire home">
              <BrandMark />
            </Link>

            <form className="search-control search-control-compact header-search" onSubmit={submitSearch}>
              <input
                className="search-control-input"
                type="text"
                value={headerQuery}
                placeholder="Search for an agent service"
                onChange={(event) => setHeaderQuery(event.target.value)}
              />
              <button type="submit" className="search-control-button" aria-label="Search">
                <Search size={18} />
              </button>
            </form>
          </div>

          <button
            type="button"
            className="mobile-menu-button"
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
            aria-controls="primary-navigation"
            onClick={() => setMobileMenuOpen((current) => !current)}
          >
            {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>

          {currentUser ? (
            <div className="header-quick-actions" aria-label="Account shortcuts">
              <NavLink to="/messages" className="header-icon-link" aria-label="Messages" onClick={closeMobileMenu}>
                <span className="header-icon-bubble">
                  <MessageSquare size={18} />
                </span>
              </NavLink>
              <NavLink to="/account" className="header-profile-link" aria-label="Account" onClick={closeMobileMenu}>
                <span className="header-profile-bubble">
                  {currentUser.user_metadata?.full_name?.slice(0, 1) || currentUser.email?.slice(0, 1) || 'A'}
                </span>
              </NavLink>
            </div>
          ) : null}

          <nav
            id="primary-navigation"
            className={`site-header-right${mobileMenuOpen ? ' open' : ''}`}
            aria-label="Primary"
          >
            <NavLink to="/explore" className="header-link" onClick={closeMobileMenu}>
              Explore
            </NavLink>
            <NavLink to="/studio" className="header-link" onClick={closeMobileMenu}>
              List your agent
            </NavLink>
            {currentUser ? (
              null
            ) : (
              <>
                <NavLink to="/login" className="header-link" onClick={closeMobileMenu}>
                  Sign in
                </NavLink>
                <Link to="/login?mode=signup" className="join-button join-button-accent" onClick={closeMobileMenu}>
                  Join
                </Link>
              </>
            )}
          </nav>
        </div>
      </header>

      <main ref={mainRef}>{children}</main>

      <footer className="site-footer">
        <div className="footer-bottom simple-footer">
          <div className="footer-brand-block">
            <BrandMark className="footer-brand" />
            <span className="footer-copyright">&copy; AgenticHire 2026</span>
          </div>
          <div className="footer-links">
            <Link to="/explore">Explore</Link>
            <Link to="/studio">List your agent</Link>
            <Link to="/how-agentichire-works">How AgenticHire works</Link>
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
